import { Button, Divider } from '@mui/material';
import { MatchStatus } from '@okletsplay/core';
import { ModalName } from 'app/enums';
import { MatchEntity } from 'app/interfaces/entity';
import { modalsReducers } from 'app/store/modal/slice';
import { sessionSelector } from 'app/store/session/selectors';
import { GameActivityPaymentProvider } from 'modules/game-payments/providers';
import { GameActivityType } from 'shared/enums';
import { MatchDetailsModalHeader } from 'modules/matches/components/MatchDetailsModalHeader';
import { AbortedMatchStatus } from 'modules/matches/components/MatchResultModal/AbortedMatchStatus';
import { MatchResultStatus } from 'modules/matches/components/MatchResultModal/MatchResultStatus';
import { MatchResultSummary } from 'modules/matches/components/MatchResultModal/MatchResultSummary';
import { useMatchResult } from 'modules/matches/hooks/use-match-result';
import React, { FC, useCallback, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Modal, ModalProps } from 'shared/components/Modal';
import { withModalOpenChecker } from 'shared/HOC/withModalOpenChecker';

type MatchResultModalProps = Partial<ModalProps> & {
  match: MatchEntity;
};

const MatchResultModalComponent: FC<MatchResultModalProps> = ({ match, onClose }) => {
  const dispatch = useDispatch();
  const currentUser = useSelector(sessionSelector.activeUser);
  const { players, matchResultsMap, playerNamesMap } = useMatchResult(match);
  const { title, icon } = match?.game ?? { title: '', icon: '' };

  const playAgainHandler = useCallback(() => {
    onClose?.();
    dispatch(modalsReducers.closeModal(ModalName.MatchResult));
    dispatch(modalsReducers.openModal({ name: ModalName.CreateMatch, data: match }));
  }, [onClose, dispatch, match]);

  const isPlayAgainButtonAvailable = useMemo(() => {
    return Boolean(players?.find((player) => player.userId === currentUser?.userId));
  }, [players, currentUser?.userId]);

  const isFinishedMatch = useMemo(() => match?.status === MatchStatus.Finished, [match?.status]);

  const content = (
    <Modal
      maxWidth="sm"
      onClose={onClose}
      contentHeight={380}
      name={ModalName.MatchResult}
      header={
        <MatchDetailsModalHeader
          title={title}
          logo={icon}
          entryFee={match.entryFee}
          status={match.status}
        />
      }
      actions={
        <>
          {isPlayAgainButtonAvailable && (
            <Button
              fullWidth
              size="medium"
              color="primary"
              variant="contained"
              onClick={playAgainHandler}
            >
              Play Again
            </Button>
          )}
        </>
      }
    >
      {isFinishedMatch ? (
        <MatchResultStatus matchResults={match.results} playerNames={playerNamesMap} />
      ) : (
        <AbortedMatchStatus />
      )}
      <Divider />
      <MatchResultSummary matchResults={matchResultsMap} players={players} />
    </Modal>
  );

  return (
    <GameActivityPaymentProvider gameActivity={match} activityType={GameActivityType.Match}>
      {content}
    </GameActivityPaymentProvider>
  );
};

export const MatchResultModal = withModalOpenChecker(
  MatchResultModalComponent,
  ModalName.MatchResult
);
